import React, { useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import Tooltip from '@material-ui/core/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PROFILES } from '../../../utils/constants';
import Button from '@mui/material/Button';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClearIcon from '@mui/icons-material/Clear';
import PhoneIcon from '@mui/icons-material/Phone';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import Container from '../../GeneralComponents/Container';
import { Topbar, Sidebar, Table } from '../components';
import { deleteService, getServices, getOrganizations } from '../../../redux/actions/apiActions';
import setGlobalLoader from '../../../redux/actions/loaderActions';
import { SERVICE_TYPES, PAYMENT_MODES } from './AddService';

// #SUGGESTION: Add a summary row or totals card showing the sum of all filtered service amounts.
// #SUGGESTION: Allow bulk export of service records to CSV/Excel for accounting purposes.

const getServiceTypeLabel = (value) => SERVICE_TYPES.find((s) => s.value === value)?.label || value;
const getPaymentModeLabel = (value) => PAYMENT_MODES.find((p) => p.value === value)?.label || (value ? value : '—');

const TableComponent = React.forwardRef(({ columns, data, title, isRemoteData }, ref) => {
    const theme = useTheme();
    return (
        <Box p={4}>
            <Box
                width={1}
                height={1}
                borderRadius={2}
                border={`2px solid ${theme.palette.divider}`}
                sx={{ borderStyle: 'dashed' }}
            >
                <Table
                    ref={ref}
                    columns={columns}
                    data={data}
                    export={false}
                    selection={false}
                    toolbar
                    title={title}
                    isRemoteData={isRemoteData}
                />
            </Box>
        </Box>
    );
});

const Services = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const profile = useSelector((state) => state.authReducer.profile);
    const tableRef = useRef(null);
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

    const [openSidebar, setOpenSidebar] = useState(false);
    const [selectedServiceType, setSelectedServiceType] = useState(null);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    // const [searchName, setSearchName] = useState('');
    const [searchMobile, setSearchMobile] = useState('');

    const today = new Date();
    const minDateObj = new Date('2025-01-01');

    const getDefaultStartDate = () => (profile === PROFILES.ADMIN ? new Date(today.getFullYear(), today.getMonth(), 1) : null);
    const getDefaultEndDate = () => (profile === PROFILES.ADMIN ? today : null);

    const [dateRange, setDateRange] = useState([
        { startDate: getDefaultStartDate(), endDate: getDefaultEndDate(), key: 'selection' },
    ]);

    const startDate = dateRange[0].startDate;
    const endDate = dateRange[0].endDate;

    const handleSidebarOpen = () => setOpenSidebar(true);
    const handleSidebarClose = () => setOpenSidebar(false);
    const open = isMd ? false : openSidebar;

    useEffect(() => {
        if (profile === PROFILES.ADMIN) {
            async function fetchOrganizations() {
                try {
                    const orgRes = await getOrganizations('?limit=10000');
                    const data = (orgRes?.data?.data?.docs || []).map((d) => ({
                        label: d?.name,
                        value: d?._id,
                    }));
                    setOrganizations(data);
                } catch (error) {
                    console.log('get organizations error', error);
                }
            }
            fetchOrganizations();
        }
    }, [profile]);

    const handleDelete = async (rowData) => {
        if (window.confirm('Are you sure you want to delete this service record?')) {
            dispatch(setGlobalLoader(true));
            try {
                await deleteService(rowData._id);
                dispatch(setGlobalLoader(false));
                toast.success('Service deleted successfully');
                if (tableRef.current) tableRef.current.onQueryChange();
            } catch (error) {
                dispatch(setGlobalLoader(false));
                console.log('delete service error', error);
                toast.error(error?.response?.data?.message ?? 'Error deleting service');
            }
        } else {
            toast.info('Request Cancelled');
        }
    };

    const columns = [
        { title: 'Sr No.', field: 'index', filtering: false },
        { title: 'Name', field: 'name', filtering: false, render: (rowData) => rowData.name || '—' },
        {
            title: 'Contact Number',
            field: 'mobileNumber',
            filtering: false,
            render: (rowData) => rowData.mobileNumber
                ? (
                    <Box
                        component="a"
                        href={`tel:${rowData.mobileNumber}`}
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                    >
                        <PhoneIcon sx={{ fontSize: 14 }} />
                        {rowData.mobileNumber}
                    </Box>
                )
                : '—',
        },
        ...(profile === PROFILES.ADMIN
            ? [{ title: 'Organization', filtering: false, render: (rowData) => rowData.organizationId?.name || '—' }]
            : []),
        { title: 'Service Type', field: 'serviceTypeLabel', filtering: false },
        { title: 'Amount (₹)', field: 'amount', filtering: false },
        { title: 'Payment Mode', field: 'paymentModeLabel', filtering: false },
        { title: 'Date', field: 'dateDisplay', filtering: false },
        { title: 'Documents', field: 'documentsCount', filtering: false },
        { title: 'Created At', field: 'createdAtDisplay', filtering: false },
        {
            title: 'Actions',
            field: '',
            filtering: false,
            render: (rowData) => (
                <div className="tableActions">
                    <Tooltip title="View">
                        <VisibilityIcon
                            onClick={() =>
                                navigate(`/services/view/${rowData._id}`, {
                                    state: { ServiceData: rowData },
                                })
                            }
                        />
                    </Tooltip>
                    {profile !== PROFILES.ADMIN && (
                        <>
                            <Tooltip title="Edit">
                                <EditIcon
                                    onClick={() =>
                                        navigate(`/services/edit/${rowData._id}`, {
                                            state: { ServiceData: rowData },
                                        })
                                    }
                                />
                            </Tooltip>
                            <Tooltip title="Delete">
                                <DeleteIcon onClick={() => handleDelete(rowData)} />
                            </Tooltip>
                        </>
                    )}
                </div>
            ),
        },
    ];

    const fetchServices = async (query) => {
        return new Promise(async (resolve) => {
            try {
                dispatch(setGlobalLoader(true));
                const page = query.page + 1;
                const limit = query.pageSize;

                const queryParams = new URLSearchParams({
                    page: page.toString(),
                    limit: limit.toString(),
                });

                // if (searchName.trim()) {
                //     queryParams.append('name', searchName.trim());
                // }
                if (searchMobile.trim()) {
                    queryParams.append('mobileNumber', searchMobile.trim());
                }
                if (selectedServiceType?.value) {
                    queryParams.append('service', selectedServiceType.value);
                }
                if (selectedPaymentMode?.value) {
                    queryParams.append('paymentMode', selectedPaymentMode.value);
                }
                if (selectedOrganization?.value) {
                    queryParams.append('organizationId', selectedOrganization.value);
                }
                if (startDate) {
                    queryParams.append('startDate', format(startDate, 'yyyy-MM-dd'));
                }
                if (endDate) {
                    queryParams.append('endDate', format(endDate, 'yyyy-MM-dd'));
                }

                const res = await getServices(`?${queryParams.toString()}`);
                const apiData = res?.data?.data;

                const data = (apiData?.docs || []).map((item, i) => ({
                    ...item,
                    index: (page - 1) * limit + i + 1,
                    serviceTypeLabel: getServiceTypeLabel(item.service),
                    paymentModeLabel: getPaymentModeLabel(item.paymentMode),
                    // studentName: item.studentId?.name || '—',
                    dateDisplay: item.date ? format(new Date(item.date), 'MMM dd, yyyy') : '—',
                    documentsCount: item.documents?.length ? `${item.documents.length} file(s)` : '—',
                    createdAtDisplay: item.createdAt ? format(new Date(item.createdAt), 'MMM dd, yyyy') : '—',
                }));

                dispatch(setGlobalLoader(false));
                resolve({ data, page: query.page, totalCount: apiData?.total || 0 });
            } catch (error) {
                dispatch(setGlobalLoader(false));
                console.log('get services error', error);
                toast.error(error?.response?.data?.message ?? 'Error fetching services');
                resolve({ data: [], page: 0, totalCount: 0 });
            }
        });
    };

    const handleFilter = () => {
        if (tableRef.current) tableRef.current.onQueryChange();
    };

    const handleReset = () => {
        // setSearchName('');
        setSearchMobile('');
        setSelectedServiceType(null);
        setSelectedPaymentMode(null);
        setSelectedOrganization(null);
        setDateRange([
            { startDate: getDefaultStartDate(), endDate: getDefaultEndDate(), key: 'selection' },
        ]);
        setShowDatePicker(false);
        setTimeout(() => {
            if (tableRef.current) tableRef.current.onQueryChange();
        }, 100);
    };

    return (
        <Box>
            <AppBar
                position={'fixed'}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
                elevation={0}
            >
                <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                    <Topbar onSidebarOpen={handleSidebarOpen} />
                </Container>
            </AppBar>
            <Sidebar onClose={handleSidebarClose} open={open} variant={isMd ? 'permanent' : 'temporary'} />
            <main>
                <Box height={{ xs: 94, sm: 102, md: 107 }} />
                <Box
                    display="flex"
                    flex="1 1 auto"
                    overflow="hidden"
                    paddingLeft={{ md: '256px' }}
                >
                    <Box display="flex" flex="1 1 auto" overflow="hidden">
                        <Box flex="1 1 auto" height="100%" overflow="auto">
                            {/* Filters */}
                            <Box px={{ xs: 3, md: 4 }} pt={3}>
                                <Typography variant="subtitle2" fontWeight={700} mb={2}>
                                    Filters
                                </Typography>
                                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                                    {/* Name Search 
                                    <TextField
                                        size="small"
                                        placeholder="Search by name"
                                        value={searchName}
                                        onChange={(e) => setSearchName(e.target.value)}
                                        sx={{ minWidth: 180 }}
                                    />
                                    */}

                                    {/* Mobile / Contact Number Search */}
                                    <TextField
                                        size="small"
                                        placeholder="Search by contact number"
                                        value={searchMobile}
                                        inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                                        onChange={(e) => setSearchMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                        sx={{ minWidth: 200 }}
                                    />

                                    {/* Date Range Picker */}
                                    <Box sx={{ position: 'relative' }}>
                                        <TextField
                                            size="small"
                                            value={
                                                startDate && endDate
                                                    ? `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
                                                    : ''
                                            }
                                            placeholder="Select date range"
                                            onClick={() => setShowDatePicker(!showDatePicker)}
                                            InputProps={{
                                                readOnly: true,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CalendarMonthIcon />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: startDate && endDate && (
                                                    <InputAdornment position="end">
                                                        <ClearIcon
                                                            sx={{ cursor: 'pointer' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
                                                                setShowDatePicker(false);
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ minWidth: 280, cursor: 'pointer' }}
                                        />
                                        {showDatePicker && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    zIndex: 1000,
                                                    mt: 1,
                                                    boxShadow: 3,
                                                    borderRadius: 1,
                                                    bgcolor: 'background.paper',
                                                }}
                                            >
                                                <DateRangePicker
                                                    onChange={(item) => setDateRange([item.selection])}
                                                    ranges={[{
                                                        startDate: dateRange[0].startDate || new Date(),
                                                        endDate: dateRange[0].endDate || new Date(),
                                                        key: 'selection',
                                                    }]}
                                                    minDate={minDateObj}
                                                    maxDate={today}
                                                    rangeColors={[theme.palette.primary.main]}
                                                    locale={enUS}
                                                />
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                        p: 1,
                                                        borderTop: `1px solid ${theme.palette.divider}`,
                                                    }}
                                                >
                                                    <Button size="small" onClick={() => setShowDatePicker(false)}>
                                                        Close
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Service Type Filter */}
                                    <Select
                                        styles={{
                                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                            control: (provided) => ({ ...provided, minHeight: 40, minWidth: 200 }),
                                        }}
                                        placeholder="Filter by Service Type"
                                        value={selectedServiceType}
                                        options={SERVICE_TYPES}
                                        isClearable
                                        onChange={(e) => setSelectedServiceType(e)}
                                    />

                                    {/* Payment Mode Filter */}
                                    <Select
                                        styles={{
                                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                            control: (provided) => ({ ...provided, minHeight: 40, minWidth: 200 }),
                                        }}
                                        placeholder="Filter by Payment Mode"
                                        value={selectedPaymentMode}
                                        options={PAYMENT_MODES}
                                        isClearable
                                        onChange={(e) => setSelectedPaymentMode(e)}
                                    />

                                    {/* Organization Filter - Admin only */}
                                    {profile === PROFILES.ADMIN && (
                                        <Select
                                            styles={{
                                                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                control: (provided) => ({ ...provided, minHeight: 40, minWidth: 250 }),
                                            }}
                                            placeholder="Select Organization"
                                            value={selectedOrganization}
                                            options={organizations}
                                            isClearable
                                            onChange={(e) => setSelectedOrganization(e)}
                                        />
                                    )}

                                    <Button variant="outlined" color="primary" size="medium" onClick={handleFilter}>
                                        Filter
                                    </Button>
                                    <Button variant="text" color="error" size="medium" onClick={handleReset}>
                                        Reset
                                    </Button>
                                </Box>
                            </Box>

                            {/* Add button - organization only */}
                            {profile === PROFILES.ORGANIZATION && (
                                <Box marginTop={3} marginLeft={{ xs: 3, md: 4 }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        onClick={() => navigate('/services/add')}
                                    >
                                        Add Service
                                    </Button>
                                </Box>
                            )}

                            <TableComponent
                                ref={tableRef}
                                data={fetchServices}
                                columns={columns}
                                title="Services"
                                isRemoteData={true}
                            />
                            <Divider />
                        </Box>
                    </Box>
                </Box>
            </main>
        </Box>
    );
};

export default Services;
